import { Capacitor } from '@capacitor/core';
import { request } from '../api/client';

/**
 * Подписка на пуш-уведомления в Android-приложении.
 *
 * В браузере ничего не делает: там другой механизм (Web Push с ключами VAPID),
 * он появится вместе с поддержкой iPhone. Плагин подгружается динамически,
 * чтобы его код не попадал в браузерную сборку.
 */
/**
 * Убирает уже показанные уведомления.
 *
 * Значок с цифрой на приложении держится, пока в шторке лежит уведомление.
 * Человек прочитал переписку — значок остаётся и обещает непрочитанное,
 * которого нет.
 */
export async function clearDeliveredNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.removeAllDeliveredNotifications();
  } catch (error) {
    console.warn('Не удалось убрать показанные уведомления', error);
  }
}

export async function registerPush(
  deviceId: string,
  onOpenChat: (chatId: string) => void,
): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // На Android 13 и новее разрешение на уведомления спрашивается отдельно
    let permission = await PushNotifications.checkPermissions();
    if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
      permission = await PushNotifications.requestPermissions();
    }
    if (permission.receive !== 'granted') {
      console.info('Уведомления не разрешены пользователем');
      return;
    }

    // Канал с высокой важностью: только при ней Android показывает всплывающую
    // плашку на разблокированном экране. С обычной важностью уведомление
    // видно на заблокированном экране, но на разблокированном молча ложится
    // в шторку — выглядит как «уведомления не работают».
    try {
      await PushNotifications.createChannel({
        id: 'messages',
        name: 'Сообщения',
        description: 'Новые сообщения в переписке',
        importance: 5,
        visibility: 1,
        vibration: true,
      });
    } catch (error) {
      console.warn('Не удалось создать канал уведомлений', error);
    }

    // Токен приходит асинхронно и может обновиться сам — например, после
    // переустановки приложения. Поэтому отправляем его серверу каждый раз.
    await PushNotifications.addListener('registration', (token) => {
      void request(`/auth/devices/${deviceId}/push-token`, {
        method: 'PUT',
        body: { token: token.value, provider: 'FCM' },
      }).catch((error) => console.warn('Не удалось сохранить токен уведомлений', error));
    });

    await PushNotifications.addListener('registrationError', (error) => {
      console.warn('Регистрация уведомлений не удалась', error);
    });

    // Нажатие на уведомление должно открывать тот чат, о котором оно было
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const chatId = action.notification.data?.chatId;
      if (typeof chatId === 'string') {
        onOpenChat(chatId);
      }
    });

    await PushNotifications.register();
  } catch (error) {
    // Отсутствие уведомлений не должно мешать пользоваться перепиской
    console.warn('Не удалось подключить уведомления', error);
  }
}
